import { useMemo, useState } from "react";

/**
 * Refund form — Phase 11.
 *
 * Three cases (likely; the server is authoritative on rejection):
 *   A. Full refund of a never-refunded transaction → loyalty fully
 *      reversed, status flips to REFUNDED.
 *   B. Partial refund → status PARTIAL_REFUND; subsequent partials
 *      stack until the cumulative `refundedAmount` equals `amount`,
 *      at which point the server flips to REFUNDED.
 *   C. Top-up partial-to-full → loyalty reversal completes when the
 *      remaining peso balance is consumed.
 *
 * The "remaining" preview math is client-side and labelled "approx";
 * the server ultimately decides via REFUND_EXCEEDS_REMAINING (or, for
 * amount-NULL legacy rows, NOT_REFUNDABLE_NO_AMOUNT).
 */
export function RefundForm(props: {
  amount: string | null;
  refundedSoFar: string | null;
  onSubmit: (amount: string, reason: string) => Promise<void>;
  busy: boolean;
}) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const remainingApprox = useMemo(() => {
    if (props.amount === null) return null;
    const total = Number(props.amount);
    const sofar = props.refundedSoFar ? Number(props.refundedSoFar) : 0;
    if (Number.isNaN(total) || Number.isNaN(sofar)) return null;
    const remaining = Math.max(0, total - sofar);
    return remaining.toFixed(2);
  }, [props.amount, props.refundedSoFar]);

  // Client-side "likely case" preview. Strictly cosmetic — the server
  // is the source of truth.
  const previewCase: "A" | "B" | "C" | null = useMemo(() => {
    if (props.amount === null) return null;
    const total = Number(props.amount);
    const sofar = props.refundedSoFar ? Number(props.refundedSoFar) : 0;
    const ask = Number(amount);
    if (Number.isNaN(total) || Number.isNaN(sofar) || Number.isNaN(ask)) {
      return null;
    }
    if (ask <= 0) return null;
    if (sofar === 0 && ask >= total) return "A";
    if (sofar > 0 && sofar + ask >= total) return "C";
    return "B";
  }, [amount, props.amount, props.refundedSoFar]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (props.amount === null) {
      setErr(
        "This transaction has no peso amount on file (legacy visit-only). It can't be refunded.",
      );
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) {
      setErr("Enter a peso value like 50 or 50.25.");
      return;
    }
    if (reason.trim().length < 5) {
      setErr("Reason must be at least 5 characters.");
      return;
    }
    try {
      await props.onSubmit(amount, reason.trim());
      setAmount("");
      setReason("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Refund failed.");
    }
  }

  const disabledLegacy = props.amount === null;

  return (
    <form onSubmit={submit} className="space-y-3">
      {disabledLegacy && (
        <div className="rounded-card border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          This transaction has no peso amount (legacy visit-only). Refund is
          unavailable.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 text-xs text-ink-dark">
        <div>
          <span className="block uppercase tracking-wider">Original</span>
          <span className="font-mono text-navy">
            {props.amount ? `₱${props.amount}` : "—"}
          </span>
        </div>
        <div>
          <span className="block uppercase tracking-wider">
            Remaining (approx)
          </span>
          <span className="font-mono text-navy">
            {remainingApprox !== null ? `₱${remainingApprox}` : "—"}
          </span>
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
          Refund amount (₱)
        </span>
        <input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
          disabled={disabledLegacy}
          placeholder="e.g. 50.00"
          className="mt-1 input"
        />
      </label>

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
          Reason
        </span>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          minLength={5}
          maxLength={200}
          disabled={disabledLegacy}
          placeholder="e.g. Customer returned half the order"
          className="mt-1 input"
        />
      </label>

      {previewCase && (
        <p className="text-xs text-ink-dark">
          Likely case <span className="font-mono">{previewCase}</span>:{" "}
          {previewCase === "A" && "full refund (loyalty fully reversed)."}
          {previewCase === "B" && "partial refund (loyalty stays for now)."}
          {previewCase === "C" &&
            "remaining balance refunded (loyalty fully reversed)."}
        </p>
      )}

      {err && (
        <div className="rounded-card border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {err}
        </div>
      )}

      <button
        type="submit"
        disabled={
          props.busy || disabledLegacy ||
          amount.trim() === "" || reason.trim().length < 5
        }
        className="btn-primary w-full"
      >
        {props.busy ? "Refunding…" : "Refund"}
      </button>
    </form>
  );
}
